import { useState, useEffect, useCallback } from 'react';
import {
  Send, Plus, Trash2, Eye, ArrowLeft, RefreshCw,
  CheckCircle, Clock, AlertCircle, FileText, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

// ─── Template Preview Cards ───
const TEMPLATE_PREVIEWS = {
  clean_minimal: { label: 'Clean Minimal', desc: 'White background, clean typography', bg: '#ffffff', text: '#333', accent: '#F06415' },
  bold_stage: { label: 'Bold Stage', desc: 'Dark background, dramatic feel', bg: '#0f172a', text: '#e2e8f0', accent: '#F06415' },
  classic_professional: { label: 'Classic Professional', desc: 'Light grey, traditional layout', bg: '#f8f9fa', text: '#212529', accent: '#F06415' },
};

const statusConfig = {
  draft: { color: 'bg-slate-100 text-slate-600', icon: FileText, label: 'Draft' },
  sending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Sending...' },
  sent: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Sent' },
  failed: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Failed' },
};

// ─── Newsletter Composer ───
function NewsletterComposer({ newsletter, onBack, onSaved }) {
  const isNew = !newsletter;
  const [subject, setSubject] = useState(newsletter?.subject || '');
  const [body, setBody] = useState(newsletter?.body || '');
  const [template, setTemplate] = useState(newsletter?.template || 'clean_minimal');
  const [recipientFilter, setRecipientFilter] = useState(newsletter?.recipient_filter || 'all');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [savedId, setSavedId] = useState(newsletter?.id || null);
  const [recipientCount, setRecipientCount] = useState(null);

  const isSent = newsletter?.status === 'sent' || newsletter?.status === 'sending';

  // Fetch recipient count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.get('/newsletters/recipients/count', { params: { filter: recipientFilter } });
        setRecipientCount(res.data.count);
      } catch { /* ignore */ }
    };
    fetchCount();
  }, [recipientFilter]);

  const handleSave = async () => {
    if (!subject.trim()) return toast.error('Subject is required');
    if (!body.trim()) return toast.error('Body content is required');
    setSaving(true);
    try {
      if (savedId) {
        // For simplicity, delete old and create new (since we don't have PUT)
        await api.delete(`/newsletters/${savedId}`);
      }
      const res = await api.post('/newsletters', { subject, body, template, recipient_filter: recipientFilter });
      setSavedId(res.data.id);
      toast.success('Newsletter saved');
      onSaved?.();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await api.post('/newsletters/preview', { subject, body, template });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch { toast.error('Preview failed'); }
  };

  const handleSendTest = async () => {
    if (!savedId) { toast.error('Save the newsletter first'); return; }
    if (!testEmail) { toast.error('Enter a test email'); return; }
    setSendingTest(true);
    try {
      await api.post(`/newsletters/${savedId}/test`, { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async () => {
    if (!savedId) { toast.error('Save the newsletter first'); return; }
    setSending(true);
    try {
      await api.post(`/newsletters/${savedId}/send`);
      toast.success('Newsletter queued for sending');
      setShowSendConfirm(false);
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="newsletter-composer">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="composer-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl font-bold text-slate-900">{isNew ? 'New Newsletter' : 'Edit Newsletter'}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-1" /> Preview
          </Button>
          {!isSent && (
            <>
              <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-draft-btn">
                {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Save Draft
              </Button>
              <Button size="sm" onClick={() => { if (!savedId) { handleSave().then(() => setShowSendConfirm(true)); } else { setShowSendConfirm(true); } }} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="send-btn">
                <Send className="w-4 h-4 mr-1" /> Send
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Your newsletter subject..."
                  disabled={isSent}
                  data-testid="subject-input"
                  className="text-lg font-medium"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Body (HTML supported)</Label>
                <Textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Write your newsletter content here... HTML tags are supported."
                  rows={14}
                  disabled={isSent}
                  data-testid="body-input"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-400">Supports HTML: &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a href=&quot;...&quot;&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;img&gt;</p>
              </div>
            </CardContent>
          </Card>

          {/* Test Send */}
          {!isSent && (
            <Card>
              <CardContent className="pt-6">
                <Label className="mb-2 block text-sm font-medium">Send Test Email</Label>
                <div className="flex gap-2">
                  <Input
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="test-email-input"
                  />
                  <Button variant="outline" onClick={handleSendTest} disabled={sendingTest || !savedId} data-testid="send-test-btn">
                    {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Send Test'}
                  </Button>
                </div>
                {!savedId && <p className="text-xs text-amber-500 mt-1">Save draft first to send a test</p>}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Template Selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wide">Template</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(TEMPLATE_PREVIEWS).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => !isSent && setTemplate(key)}
                  data-testid={`template-${key}`}
                  disabled={isSent}
                  className={cn(
                    "w-full p-3 rounded-lg border-2 text-left transition-all",
                    template === key ? "border-orange-400 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.bg, border: '1px solid #e5e7eb' }}>
                      <span style={{ color: t.accent, fontWeight: 800, fontSize: 10 }}>BOX</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{t.label}</p>
                      <p className="text-xs text-slate-400">{t.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Recipients */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wide">Recipients</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={recipientFilter} onValueChange={v => !isSent && setRecipientFilter(v)} disabled={isSent}>
                <SelectTrigger data-testid="recipient-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  <SelectItem value="starter">Starter Package</SelectItem>
                  <SelectItem value="pro">Pro Package</SelectItem>
                  <SelectItem value="max">Max Package</SelectItem>
                </SelectContent>
              </Select>
              {recipientCount !== null && (
                <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="recipient-count">
                  <Users className="w-3 h-3" /> {recipientCount} site{recipientCount !== 1 ? 's' : ''} matched
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>This is how your newsletter will appear in recipients' inboxes</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg">
            <iframe
              srcDoc={previewHtml}
              title="Newsletter Preview"
              className="w-full h-[500px] border-0"
              data-testid="preview-iframe"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Newsletter?</DialogTitle>
            <DialogDescription>
              This will send "{subject}" to {recipientFilter === 'all' ? 'all clients' : `${recipientFilter} package clients`}
              {recipientCount !== null && ` (~${recipientCount} sites)`}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendConfirm(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="confirm-send-btn">
              {sending ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {sending ? 'Sending...' : 'Confirm Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Newsletter List ───
function NewsletterList({ onCompose, onEdit }) {
  const [newsletters, setNewsletters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNewsletters = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/newsletters');
      setNewsletters(res.data.newsletters || []);
    } catch { toast.error('Failed to load newsletters'); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchNewsletters(); }, [fetchNewsletters]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/newsletters/${id}`);
      setNewsletters(prev => prev.filter(n => n.id !== id));
      toast.success('Newsletter deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="newsletter-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Newsletters</h2>
          <p className="text-sm text-slate-500 mt-0.5">{newsletters.length} newsletter{newsletters.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={onCompose} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="compose-btn">
          <Plus className="w-4 h-4 mr-1" /> Compose
        </Button>
      </div>

      {newsletters.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Send className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">No newsletters yet</h3>
            <p className="text-sm text-slate-400 mb-4">Create your first newsletter to email your clients</p>
            <Button onClick={onCompose} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1" /> Compose Newsletter
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {newsletters.map(nl => {
            const st = statusConfig[nl.status] || statusConfig.draft;
            const StIcon = st.icon;
            return (
              <Card key={nl.id} className="hover:shadow-sm transition-shadow cursor-pointer" data-testid={`newsletter-item-${nl.id}`}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4" onClick={() => onEdit(nl)}>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-800 truncate">{nl.subject}</h3>
                        <Badge className={cn("text-xs", st.color)}>
                          <StIcon className="w-3 h-3 mr-1" />{st.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span>{formatDate(nl.created_at)}</span>
                        <span className="capitalize">{nl.template?.replace('_', ' ')}</span>
                        <span className="capitalize">{nl.recipient_filter === 'all' ? 'All clients' : `${nl.recipient_filter} package`}</span>
                        {nl.status === 'sent' && (
                          <span className="text-emerald-600 font-medium">
                            Sent to {nl.sent_count}/{nl.total_recipients}
                            {nl.failed_count > 0 && <span className="text-red-500 ml-1">({nl.failed_count} failed)</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {nl.status === 'draft' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(nl.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-newsletter-${nl.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Export ───
export default function NewslettersView() {
  const [mode, setMode] = useState('list'); // list | compose
  const [editingNewsletter, setEditingNewsletter] = useState(null);

  if (mode === 'compose') {
    return (
      <NewsletterComposer
        newsletter={editingNewsletter}
        onBack={() => { setMode('list'); setEditingNewsletter(null); }}
        onSaved={() => {}}
      />
    );
  }

  return (
    <NewsletterList
      onCompose={() => { setEditingNewsletter(null); setMode('compose'); }}
      onEdit={(nl) => { setEditingNewsletter(nl); setMode('compose'); }}
    />
  );
}
